import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';

import { useUserContext } from 'providers/UserProvider';
import useFormValidation from 'hooks/useFormValidation';

import { returnManifestApi, shipmentApi } from 'api';

const validationSchema = {
  shippingProvider: {
    required: 'Shipping Provider is required',
  },
};

const initialValues = {
  shippingProvider: '',
};

const CreateReturnManifestModal = ({ show, onHide, onSuccess }) => {
  const { selectedWarehouse } = useUserContext();

  const [shippingProviders, setShippingProviders] = useState([]);

  const [isLoadingShippingProviders, setIsLoadingShippingProviders] =
    useState(false);

  const [shippingProvidersError, setShippingProvidersError] = useState(null);

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    validateForm,
    resetForm,
    isValid,
  } = useFormValidation(initialValues, validationSchema);

  useEffect(() => {
    const fetchInitialData = async () => {
      if (!show) return;

      setIsLoadingShippingProviders(true);

      try {
        const providerResponse = await shipmentApi.getShippingProviders({
          is_active: true,
        });

        setShippingProviders(providerResponse || []);
      } catch (error) {
        toast.error('Failed to load data');

        setShippingProvidersError('Failed to load shipping providers');
      } finally {
        setIsLoadingShippingProviders(false);
      }
    };

    fetchInitialData();
  }, [show]);

  const handleProviderChange = e => {
    const providerId = e.target.value;

    handleChange('shippingProvider', providerId);
  };

  const handleCreateReturnManifest = async () => {
    if (!validateForm()) return;

    try {
      await returnManifestApi.create({
        warehouse: selectedWarehouse?.id,
        shipping_provider: Number(values.shippingProvider),
      });

      toast.success('Return Manifest created successfully');
      closeModal();
      onSuccess();
    } catch (error) {
      toast.error('Failed to create return manifest');
    }
  };

  const closeModal = () => {
    resetForm();
    onHide();
  };

  const renderSelectOptions = (isLoading, error, options, optionName) => {
    if (isLoading) {
      return <option value="">Loading...</option>;
    }
    if (error) {
      return <option value="">{error}</option>;
    }

    return options.map(option => (
      <option key={option.id} value={option.id}>
        {option[optionName]}
      </option>
    ));
  };

  return (
    <Modal show={show} onHide={closeModal}>
      <Modal.Header closeButton>
        <Modal.Title>Create Return Manifests</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form>
          <Form.Group>
            <Form.Label>
              Shipping Provider<span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              value={values.shippingProvider}
              onChange={handleProviderChange}
              onBlur={() => handleBlur('shippingProvider')}
              isInvalid={touched.shippingProvider && errors.shippingProvider}
              disabled={isLoadingShippingProviders}
            >
              <option value="">Select Provider...</option>
              {renderSelectOptions(
                isLoadingShippingProviders,
                shippingProvidersError,
                shippingProviders,
                'shipping_provider_type',
              )}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {errors.shippingProvider}
            </Form.Control.Feedback>
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={resetForm}>
          Clear
        </Button>
        <Button
          variant="primary"
          onClick={handleCreateReturnManifest}
          disabled={!isValid}
        >
          Create
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateReturnManifestModal;
